import React, { useState, useEffect, useCallback } from 'react';
import { Clock, MapPin, Search, Download, RefreshCw, ChevronLeft, ChevronRight, Zap, Filter } from 'lucide-react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function HistoryPage() {
  const navigate = useNavigate();
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/leads/history');
      setHistory(r.data.history||[]);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const cols = ['location','radius','keyword','min_rating','results_count','searched_at'];
    const csv  = [cols.join(','), ...history.map(h=>cols.map(c=>`"${(h[c]||'').toString().replace(/"/g,'""')}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = 'search_history.csv'; a.click();
  };

  const filtered = history.filter(h => !search || h.location?.toLowerCase().includes(search.toLowerCase()) || h.keyword?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ maxWidth:1200, margin:'0 auto', padding:'20px' }}>
      <style>{`
        @media(max-width:640px){ .hide-mobile{display:none!important;} }
      `}</style>

      {/* Header */}
      <div className="flex-between mb-20" style={{ flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:4 }}>Search History</h1>
          <p style={{ color:'var(--text3)', fontSize:14 }}><strong style={{ color:'var(--accent)' }}>{history.length}</strong> searches recorded</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button>
          {/* <button className="btn btn-success btn-sm" onClick={exportCSV} disabled={!history.length}><Download size={13}/> Export CSV</button> */}
          <button className="btn btn-primary btn-sm" onClick={()=>navigate('/search')}><Search size={13}/> New Search</button>
        </div>
      </div>

      {/* Stats */}
      {history.length>0&&(
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
          {[
            { label:'Total Searches',  val:history.length,                                      color:'var(--accent)' },
            { label:'Total Leads',     val:history.reduce((s,h)=>s+(h.results_count||0),0).toLocaleString(), color:'var(--blue)' },
            { label:'Avg Results',     val:Math.round(history.reduce((s,h)=>s+(h.results_count||0),0)/history.length), color:'var(--purple)' },
            { label:'Avg Speed',       val:`${(history.reduce((s,h)=>s+(h.search_duration_ms||0),0)/history.length/1000).toFixed(1)}s`, color:'var(--green)' },
          ].map(s=>(
            <div key={s.label} className="card card-sm" style={{ textAlign:'center', padding:'14px 12px' }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:s.color, marginBottom:2 }}>{s.val}</div>
              <div style={{ fontSize:11.5, color:'var(--text3)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search filter */}
      <div className="toolbar mb-16">
        <div className="toolbar-left">
          <div className="input-group" style={{ minWidth:200, flex:1, maxWidth:320 }}>
            <Search size={14} className="input-icon"/>
            <input className="input" placeholder="Filter by location or keyword…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          {search&&<button className="btn btn-ghost btn-sm" onClick={()=>setSearch('')}>Clear</button>}
        </div>
        <span style={{ fontSize:13, color:'var(--text3)' }}>{filtered.length} results</span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="empty-state"><div className="spinner spinner-lg"/></div>
      ) : history.length===0 ? (
        <div className="empty-state">
          <div style={{ fontSize:48 }}>🕐</div>
          <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:18 }}>No search history</div>
          <div style={{ color:'var(--text3)', fontSize:13.5, maxWidth:320, lineHeight:1.6 }}>Your searches will appear here after you use the lead search tool.</div>
          <button className="btn btn-primary" onClick={()=>navigate('/search')}><Search size={14}/> Search Now</button>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div style={{ display:'none' }} className="history-cards">
            {filtered.map(h=>{
              const types = typeof h.business_types==='string'?JSON.parse(h.business_types||'[]'):h.business_types||[];
              return (
                <div key={h.id} className="card" style={{ padding:'14px 16px', marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                    <div style={{ width:36, height:36, borderRadius:9, background:'var(--accent-bg)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <MapPin size={16} style={{ color:'var(--accent)' }}/>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:14, marginBottom:2 }}>{h.location}</div>
                      <div style={{ fontSize:12, color:'var(--text3)' }}>{new Date(h.searched_at).toLocaleString('en-IN',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color:'var(--accent)' }}>{h.results_count}</div>
                      <div style={{ fontSize:11, color:'var(--text3)' }}>leads</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <span className="badge badge-accent">{h.radius>=1000?`${h.radius/1000}km`:`${h.radius}m`}</span>
                    {h.keyword&&<span className="badge badge-gray">🔍 {h.keyword}</span>}
                    {h.min_rating>0&&<span className="badge badge-gold">{h.min_rating}★+</span>}
                    {types.slice(0,2).map(t=><span key={t} style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:4, padding:'1px 6px', fontSize:10.5, color:'var(--text3)', textTransform:'capitalize' }}>{t.replace(/_/g,' ')}</span>)}
                    {types.length>2&&<span style={{ fontSize:11, color:'var(--text3)' }}>+{types.length-2}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: table */}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Radius</th>
                  <th className="hide-mobile">Business Types</th>
                  <th className="hide-mobile">Keyword</th>
                  <th className="hide-mobile">Min Rating</th>
                  <th>Results</th>
                  <th className="hide-mobile">Duration</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(h => {
                  const types = typeof h.business_types==='string'?JSON.parse(h.business_types||'[]'):h.business_types||[];
                  return (
                    <tr key={h.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <MapPin size={13} style={{ color:'var(--accent)', flexShrink:0 }}/>
                          <span style={{ fontWeight:600, fontSize:13.5 }}>{h.location}</span>
                        </div>
                      </td>
                      <td><span className="badge badge-accent">{h.radius>=1000?`${h.radius/1000}km`:`${h.radius}m`}</span></td>
                      <td className="hide-mobile">
                        <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                          {types.slice(0,3).map(t=><span key={t} style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:4, padding:'1px 6px', fontSize:10.5, color:'var(--text3)', textTransform:'capitalize' }}>{t.replace(/_/g,' ')}</span>)}
                          {types.length>3&&<span style={{ fontSize:11, color:'var(--text3)' }}>+{types.length-3}</span>}
                          {types.length===0&&<span style={{ fontSize:12, color:'var(--text3)' }}>All types</span>}
                        </div>
                      </td>
                      <td className="hide-mobile"><span style={{ fontSize:13, color:'var(--text2)' }}>{h.keyword||<span style={{ color:'var(--text3)' }}>—</span>}</span></td>
                      <td className="hide-mobile">{h.min_rating>0?<span className="badge badge-gold">{h.min_rating}★+</span>:<span style={{ color:'var(--text3)' }}>Any</span>}</td>
                      <td><span style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:15, color:'var(--accent)' }}>{(h.results_count||0).toLocaleString()}</span></td>
                      <td className="hide-mobile"><span style={{ fontSize:12.5, color:'var(--text3)' }}>{h.search_duration_ms?`${(h.search_duration_ms/1000).toFixed(1)}s`:'—'}</span></td>
                      <td><span style={{ fontSize:12.5, color:'var(--text3)', whiteSpace:'nowrap' }}>{new Date(h.searched_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <style>{`
        @media(max-width:640px){
          .history-cards{display:block!important;}
          .table-wrap{display:none;}
        }
      `}</style>
    </div>
  );
}